import React, { useState } from 'react';
import styled from 'styled-components';
import PaystackPop from "@paystack/inline-js";
import Swal from "sweetalert2";

// --- Styled Components ---

const Container = styled.div`
  display: flex;
  flex-direction: column;
  gap: 10px;
  width: 100%;
  max-width: 650px;
  margin: 0 auto;
  font-family: 'Segoe UI', Tahoma, Geneva, Verdana, sans-serif;
`;

const Card = styled.div`
  background: #ffffff;
  width: 100%;
  border-radius: 10px;
  box-shadow: 0 4px 15px rgba(2, 132, 199, 0.05);
  overflow: hidden;
  border: 1px solid #bae6fd;
`;

const Header = styled.div`
  background: linear-gradient(135deg, #0284c7 0%, #0369a1 100%);
  color: #ffffff;
  padding: 10px;
  text-align: center;

  h2 {
    margin: 0 0 4px 0;
    font-size: 1.25rem;
    font-weight: 700;
  }

  p {
    margin: 0;
    font-size: 0.85rem;
    opacity: 0.9;
  }
`;

const Content = styled.div`
  padding: 10px;
  display: flex;
  flex-direction: column;
  gap: 10px;
`;

const SectionTitle = styled.h3`
  font-size: 1rem;
  color: #0f172a;
  margin: 0;
  font-weight: 600;
  display: flex;
  align-items: center;
  gap: 6px;
`;

const RequirementList = styled.ul`
  list-style: none;
  padding: 0;
  margin: 0;
  display: flex;
  flex-direction: column;
  gap: 6px;
`;

const RequirementItem = styled.li`
  display: flex;
  align-items: flex-start;
  gap: 8px;
  padding: 6px 0;
  border-bottom: 1px solid #e2e8f0;
  color: #334155;
  font-size: 0.85rem;

  &:last-child {
    border-bottom: none;
  }

  svg {
    color: #0284c7;
    flex-shrink: 0;
    margin-top: 2px;
  }
`;

const PricingBox = styled.div`
  background: #f0f9ff;
  border: 1px solid #bae6fd;
  border-radius: 8px;
  padding: 10px;
  margin: 0;

  h4 {
    margin: 0 0 4px 0;
    color: #0369a1;
    font-size: 0.95rem;
  }

  p {
    margin: 0;
    color: #0369a1;
    font-size: 0.85rem;
    line-height: 1.4;

    strong {
      color: #0f172a;
    }
  }
`;

const PaymentBox = styled.div`
  background: #f8fafc;
  border: 1px solid #e2e8f0;
  border-radius: 8px;
  padding: 10px;
  margin: 0;
`;

const BankDetailGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(150px, 1fr));
  gap: 10px;
  margin-bottom: 8px;
`;

const DetailItem = styled.div`
  span {
    display: block;
    font-size: 0.75rem;
    color: #64748b;
    text-transform: uppercase;
    letter-spacing: 0.5px;
    margin-bottom: 2px;
  }

  strong {
    font-size: 0.9rem;
    color: #0f172a;
  }
`;

const EmailNotice = styled.div`
  margin-top: 8px;
  padding-top: 8px;
  border-top: 1px dashed #cbd5e1;
  font-size: 0.85rem;
  color: #334155;

  a {
    color: #0284c7;
    text-decoration: none;
    font-weight: 600;

    &:hover {
      text-decoration: underline;
    }
  }
`;

const CardLogosContainer = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  margin-bottom: 8px;
  padding-bottom: 8px;
  border-bottom: 1px solid #f1f5f9;

  img {
    height: 28px;
    object-fit: contain;
    filter: drop-shadow(0 2px 4px rgba(0, 0, 0, 0.05));
  }
`;

const Form = styled.form`
  display: flex;
  flex-direction: column;
  gap: 10px;
`;

const FormGroup = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;

  label {
    font-size: 0.85rem;
    font-weight: 600;
    color: #334155;
    word-break: break-word;
  }

  input, select {
    padding: 8px 10px;
    border: 1px solid #cbd5e1;
    border-radius: 6px;
    font-size: 0.85rem;
    color: #0f172a;
    outline: none;
    transition: all 0.2s ease;
    background: #f8fafc;
    width: 100%;
    box-sizing: border-box;

    &:focus {
      border-color: #0284c7;
      background: #ffffff;
      box-shadow: 0 0 0 2px rgba(2, 132, 199, 0.15);
    }
  }
`;

const SummaryBox = styled.div`
  background: #f1f5f9;
  border-radius: 6px;
  padding: 8px 10px;
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-top: 4px;

  span {
    font-size: 0.85rem;
    color: #475569;
    font-weight: 600;
  }

  strong {
    font-size: 1.1rem;
    color: #0f172a;
    font-weight: 800;
  }
`;

const PayButton = styled.button`
  background: linear-gradient(135deg, #0284c7 0%, #0369a1 100%);
  color: #ffffff;
  border: none;
  border-radius: 6px;
  padding: 10px;
  font-size: 0.95rem;
  font-weight: 700;
  cursor: pointer;
  transition: all 0.2s ease;
  box-shadow: 0 2px 8px rgba(2, 132, 199, 0.25);
  margin-top: 4px;
  word-break: break-word;

  &:hover {
    opacity: 0.95;
  }
`;

// --- Component ---

export default function AdultClassRegistrationAndPayment({amount}) {
  const [deliveryDetails, setDeliveryDetails] = useState({
    name: '',
    email: '',
  });

  const flatAmount = 50000;

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setDeliveryDetails((prev) => ({ ...prev, [name]: value }));
  };

  const payWithPaystack = (e) => {
    e.preventDefault();

    if (!deliveryDetails.name || !deliveryDetails.email) {
      Swal.fire({ icon: "warning", text: "Please fill in your name and email.", showConfirmButton: true });
      return;
    }

    const paystack = new PaystackPop();
    paystack.newTransaction({
      key: "pk_live_3626fe7772aaca28a10724ebb1f9727dfcc5d6cb",
      amount: amount * 100,
      email: deliveryDetails.email,
      firstname: deliveryDetails.name,
      channels: ['card'],
      onSuccess: () => {
        Swal.fire({
          icon: "success",
          text: "Payment successful!",
          showConfirmButton: true,
        });

        setDeliveryDetails({
          name: '',
          email: '',
        });
      },
      onCancel: () => {
        Swal.fire({ icon: "error", text: "Payment cancelled.", showConfirmButton: true });
      },
      onError: (error) => {
        Swal.fire({ icon: "error", text: `Payment failed: ${error.message}`, showConfirmButton: true });
      }
    });
  };

  return (
    <Container>
      {/* Notice Card */}
      <Card>
        <Header>
          <h2>Training Payment</h2>
          <p>Please review the requirements and payment instructions carefully.</p>
        </Header>
        <Content>

          <PricingBox>
            <h4>Investment</h4>
            <p>
              Tuition is a flat rate of <strong>₦ {Number(amount).toLocaleString()}</strong> per participant per batch.
            </p>
          </PricingBox>

          <PaymentBox>
            <SectionTitle style={{ fontSize: '0.95rem', marginBottom: '8px' }}>Bank Payment Details</SectionTitle>
            <BankDetailGrid>
              <DetailItem>
                <span>Account Name</span>
                <strong>Elexdon Digital Technologies Ltd</strong>
              </DetailItem>
              <DetailItem>
                <span>Account Number</span>
                <strong>1221913704</strong>
              </DetailItem>
              <DetailItem>
                <span>Bank Name</span>
                <strong>Zenith Bank</strong>
              </DetailItem>
            </BankDetailGrid>

            <EmailNotice>
              Please send evidence of payment to{' '}
              <a href="mailto:sales@elexdondigitalacademy.com">
                sales@elexdondigitalacademy.com
              </a>
            </EmailNotice>
          </PaymentBox>
        </Content>
      </Card>

      {/* Online Payment Card */}
      <Card>
        <Header>
          <h2>Online Payment</h2>
          <p>Secure card payment for your online class batch</p>
        </Header>
        <Content>
          <CardLogosContainer>
            <img 
              src="https://upload.wikimedia.org/wikipedia/commons/2/2a/Mastercard-logo.svg" 
              alt="Mastercard" 
            />
          </CardLogosContainer>

          <Form onSubmit={payWithPaystack}>
            <FormGroup>
              <label htmlFor="name">Full Name</label>
              <input 
                type="text" 
                id="name" 
                name="name" 
                placeholder="Enter your full name" 
                value={deliveryDetails.name}
                onChange={handleInputChange}
                required
              />
            </FormGroup>

            <FormGroup>
              <label htmlFor="email">Email Address</label>
              <input 
                type="email" 
                id="email" 
                name="email" 
                placeholder="Enter your email address" 
                value={deliveryDetails.email}
                onChange={handleInputChange}
                required
              />
            </FormGroup>

            <SummaryBox>
              <span>Total Amount:</span>
              <strong>N{amount?.toLocaleString()}</strong>
            </SummaryBox>

            <PayButton type="submit">
              Pay N{amount?.toLocaleString()}
            </PayButton>
          </Form>
        </Content>
      </Card>
    </Container>
  );
}